import React, { useEffect, useState } from "react";
import styled from "styled-components";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import CachedIcon from "@material-ui/icons/Cached";
import { head, isEmpty, isEqual, isUndefined, pick } from "lodash";
import moment from "moment";
import { useHovered } from "@sonder/ui/source/hooks/useHovered";
import { CircleX } from "@sonder/ui/source/icons";
import { DateTimePicker } from "@sonder/ui/source/styled/components/DateTimePicker";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import { ButtonProps } from "@sonder/ui/source/styled/fragments/Button";
import { CancelButton } from "@sonder/ui/source/styled/fragments/CancelButton";
import { InputGroup } from "@sonder/ui/source/styled/fragments/InputGroup";
import { Label } from "@sonder/ui/source/styled/fragments/Label";
import { LabeledIconButton } from "@sonder/ui/source/styled/fragments/LabeledIconButton";
import { PlainButton } from "@sonder/ui/source/styled/fragments/PlainButton";
import { Stack } from "@sonder/ui/source/styled/fragments/Stack";
import { DropdownContentAlignment } from "@sonder/ui/source/styled/surfaces/DropdownContent";
import { colors } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { useErrorReporterFiltered } from "core/hooks/useErrorReporterFiltered";
import { TaskRecurrenceSetting, TaskSubjectType } from "core/queries/GetTask";
import {
  UpdateTaskResponse,
  UpdateTaskVariables,
  updateTaskOptimisticResponse,
  useUpdateTask,
} from "core/queries/UpdateTask";
import { SetNextTurnoverRow } from "tasks/components/SetNextTurnoverRow";
import {
  PartialRecurrenceSetting,
  TaskRecurrence,
} from "tasks/components/TaskRecurrence";
import {
  RecurrenceIntervalUnit,
  TaskRecurrenceSettingsInput,
} from "typings/globalGQLTypes";

// Fragments
interface StyledDateTimePickerProps {
  isTurnoverDisabled: boolean;
}
const StyledDateTimePicker = styled(DateTimePicker)<StyledDateTimePickerProps>`
  background: ${colors.white};
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: ${props => (!props.isTurnoverDisabled ? "28rem" : "17rem")};
`;

const InlineTrigger = styled(PlainButton)`
  > svg {
    margin-right: 0.3rem;
  }
  &:hover {
    color: ${colors.black};
    > svg .outline {
      stroke-dasharray: 0;
    }
  }
`;
const RecurrenceIcon = styled(CachedIcon)`
  color: ${colors.blue7};
`;

export interface RenderTriggerArgs {
  date: string | null;
  isRecurring: boolean;
  onClear?(): void;
  title?: string;
}

// Helpers
const getDescriptionFromDate = (date: moment.Moment | null) => {
  if (!date) {
    return null;
  }

  if (moment(date).toString() === moment().endOf("day").toString()) {
    return "Today";
  }

  return date.calendar(moment(), {
    sameDay: "h:mm A",
    nextDay: "[Tomorrow]",
    lastDay: "[Yesterday]",
    nextWeek: "MMM D",
    lastWeek: "MMM D",
    sameElse: "MMM D",
  });
};

// If no date has been set, default to end of day
const getDefaultDueBy = (date: moment.Moment | null) =>
  date || moment().endOf("day");

const getDefaultRecurrence = (recurrence: TaskRecurrenceSetting | null) =>
  recurrence
    ? pick(recurrence, ["intervalUnit", "intervalAmt", "daysOfWeek"])
    : null;

const areRecurrencesEqual = (
  taskRecurrence: TaskRecurrenceSetting | null,
  selectedRecurrence: PartialRecurrenceSetting | null,
) => {
  const partialTask = getDefaultRecurrence(taskRecurrence);
  return isEqual(partialTask, selectedRecurrence);
};

const getDueDateChanges = (
  taskDueDate: moment.Moment | null,
  selectedDueDate: moment.Moment | null,
  userChangedDateFlag: boolean,
) => {
  if (userChangedDateFlag) {
    // Due date => no due date
    if (taskDueDate && !selectedDueDate) return null;
    // No due date => due date
    if (!taskDueDate && selectedDueDate) return selectedDueDate;
    // Due date => different due date
    if (
      taskDueDate &&
      selectedDueDate &&
      !taskDueDate.isSame(selectedDueDate)
    ) {
      return selectedDueDate;
    }
  }
  // No due date => no due date
  // Same due date => same due date
  return undefined;
};

const getRecurrenceSettingsChanges = (
  taskRecurrence: TaskRecurrenceSetting | null,
  selectedRecurrence: PartialRecurrenceSetting | null,
): TaskRecurrenceSettingsInput | undefined => {
  // Recurrence => no recurrence
  if (taskRecurrence && !selectedRecurrence) return { intervalAmt: null };
  // No recurrence => recurrence
  if (!taskRecurrence && selectedRecurrence) return selectedRecurrence;
  // Recurrence => different recurrence
  if (
    taskRecurrence &&
    selectedRecurrence &&
    !areRecurrencesEqual(taskRecurrence, selectedRecurrence)
  ) {
    return selectedRecurrence;
  }
  // No recurrence => no recurrence
  // Same recurrence => same recurrence
  return undefined;
};

// Components
interface TaskDueByDateTriggerProps extends RenderTriggerArgs, ButtonProps {
  disabled?: boolean;
}

export const TaskDueByDateTrigger = ({
  disabled,
  date,
  isRecurring,
  onClick,
  onClear,
}: TaskDueByDateTriggerProps) => {
  const { hovered, onMouseEnter, onMouseLeave } = useHovered();
  return (
    <LabeledIconButton
      data-testid="task-due-by-date-trigger"
      disabled={disabled}
      type="button"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {isRecurring ? <RecurrenceIcon /> : <AccessTimeIcon />}
      <InputGroup>
        <Stack>
          <Stack>
            {date && <Label>Due by</Label>}
            {date || <span>Due Date</span>}
          </Stack>
        </Stack>
        {date && hovered && onClear && (
          <CancelButton
            onClick={e => {
              onClear();
              e.stopPropagation();
            }}
          >
            <CircleX />
          </CancelButton>
        )}
      </InputGroup>
    </LabeledIconButton>
  );
};

export const TaskDueByDateInlineTrigger = ({
  date,
  disabled,
  isRecurring,
  onClick,
  title = "Due Time",
}: TaskDueByDateTriggerProps) => {
  return (
    <InlineTrigger disabled={disabled} onClick={onClick}>
      {!date && (
        <>
          <AccessTimeIcon />
          <span>{title}</span>
        </>
      )}

      {date && isRecurring && <RecurrenceIcon />}

      {date}
    </InlineTrigger>
  );
};
interface TaskDueByDateProps {
  alignContent?: DropdownContentAlignment;
  className?: string;
  bookingInfo: BookingInfo | null | undefined;
  date: moment.Moment | null;
  disabled?: boolean;
  eventName: string;
  futureCheckouts: moment.Moment[] | null;
  isTurnoverDisabled?: boolean;
  onChange?(nextSettings: UpdateTaskVariables | null): void;
  recurrenceSetting: TaskRecurrenceSetting | null;
  renderTrigger: (args: TaskDueByDateTriggerProps) => JSX.Element;
  subjectType?: TaskSubjectType;
  taskId: string;
}

export const TaskDueByDate = ({
  alignContent,
  bookingInfo,
  className,
  date,
  disabled,
  eventName,
  futureCheckouts,
  isTurnoverDisabled = false,
  onChange,
  recurrenceSetting,
  renderTrigger,
  subjectType,
  taskId,
}: TaskDueByDateProps) => {
  const closestFutureCheckout = head(futureCheckouts) || moment();
  const errorReporter = useErrorReporterFiltered();

  const { track } = useSegment();
  const [onUpdateTask] = useUpdateTask({
    refetchQueries: ["GetTasks"],
  });
  const [selectedDate, setSelectedDate] = useState<moment.Moment | null>(
    getDefaultDueBy(date),
  );
  const [selectedRecurrence, setSelectedRecurrence] = useState(
    getDefaultRecurrence(recurrenceSetting),
  );

  // Flag to track if a user has manually set a new due date for a task. This is
  // necessary to prevent accidental due date changes when a user opens and
  // closes the due date picker without actually changing the date.
  const [userChangedDateFlag, setUserChangedDateFlag] = useState(false);

  // Reset local state whenever task due date is changed in other components
  const dateDependencyKey = date && date.toISOString();
  useEffect(() => {
    setSelectedDate(getDefaultDueBy(date));
    setUserChangedDateFlag(false);
  }, [dateDependencyKey]);
  const recurrenceDependencyKey = recurrenceSetting && recurrenceSetting.id;
  useEffect(() => {
    setSelectedRecurrence(getDefaultRecurrence(recurrenceSetting));
  }, [recurrenceDependencyKey]);

  const [applyChangesFlag, setApplyChangesFlag] = useState(false);
  useEffect(() => {
    if (applyChangesFlag) {
      const nextRecurrence = getRecurrenceSettingsChanges(
        recurrenceSetting,
        selectedRecurrence,
      );

      const nextDueBy = getDueDateChanges(
        date,
        selectedDate,
        // Users aren't allowed to set a recurrence without a due date. Flip the
        // user date change flag here to ensure we default to EOD.
        userChangedDateFlag || !isEmpty(nextRecurrence),
      );

      // Only update task/invoke callback if something changed
      if (!isUndefined(nextDueBy) || !isEmpty(nextRecurrence)) {
        const mutationVariables: UpdateTaskVariables = {
          taskId,
          dueBy: nextDueBy,
          recurrenceSettings: nextRecurrence,
        };
        if (onChange) {
          onChange(mutationVariables);
        } else {
          onUpdateTask({
            variables: mutationVariables,
            optimisticResponse: updateTaskOptimisticResponse({
              id: taskId,
              dueAt: nextDueBy,
              tomTaskRecurrenceSetting: nextRecurrence
                ? {
                    __typename:
                      "TaskRecurrenceSetting" as TaskRecurrenceSetting["__typename"],
                    id: `-${taskId}`,
                    daysOfWeek: nextRecurrence.daysOfWeek || null,
                    intervalAmt: nextRecurrence.intervalAmt || 1,
                    intervalUnit:
                      nextRecurrence.intervalUnit ||
                      RecurrenceIntervalUnit.week,
                  }
                : null,
            }) as UpdateTaskResponse,
          }).catch(error => {
            errorReporter.notify(error);
          });
        }

        // Reset the user interaction flag
        setUserChangedDateFlag(false);
      }
      setApplyChangesFlag(false);
    }
  }, [applyChangesFlag]);

  return (
    <Dropdown
      className={className}
      trigger={renderTrigger({
        date: getDescriptionFromDate(date),
        disabled,
        isRecurring: !isEmpty(recurrenceSetting),
        onClear: () => {
          track({
            eventName,
            action: "click",
            element: "due_by_selector.clear",
            props: { taskId },
          });
          setSelectedDate(null);
          setSelectedRecurrence(null);
          setUserChangedDateFlag(true);
          setApplyChangesFlag(true);
        },
      })}
      onClose={() => {
        track({
          eventName,
          action: "click",
          element: "due_by_selector.dropdown_trigger",
          props: { nextValue: "closed", taskId },
        });
        setApplyChangesFlag(true);
      }}
      onOpen={() =>
        track({
          eventName,
          action: "click",
          element: "due_by_selector.dropdown_trigger",
          props: { nextValue: "opened", taskId },
        })
      }
    >
      {({ isOpen, closePortal }) => {
        const onHandleApply = (
          nextDate: moment.Moment,
          shouldClose?: boolean,
        ) => {
          setUserChangedDateFlag(true);
          setSelectedDate(nextDate);
          setApplyChangesFlag(true);
          if (shouldClose) {
            closePortal();
          }
        };
        return (
          isOpen && (
            <DropdownContent
              align={alignContent}
              data-testid="task-due-by-date-dropdown-content"
            >
              <StyledDateTimePicker
                date={selectedDate}
                isTurnoverDisabled={isTurnoverDisabled}
                recurrenceSettings={selectedRecurrence}
                onCloseTimePicker={() =>
                  track({
                    eventName,
                    action: "click",
                    element: "due_by_selector.time_picker_dropdown_trigger",
                    props: { nextValue: "closed", taskId },
                  })
                }
                onDateChange={(nextDate: moment.Moment) => {
                  onHandleApply(nextDate, true);
                }}
                onOpenTimePicker={() =>
                  track({
                    eventName,
                    action: "click",
                    element: "due_by_selector.time_picker_dropdown_trigger",
                    props: { nextValue: "opened", taskId },
                  })
                }
                onTimeChange={(nextDate: moment.Moment) => {
                  onHandleApply(nextDate);
                }}
              >
                {subjectType && (
                  <TaskRecurrence
                    recurrence={selectedRecurrence}
                    setRecurrence={setSelectedRecurrence}
                    subjectType={subjectType}
                  />
                )}
                {!isTurnoverDisabled && (
                  <SetNextTurnoverRow
                    bookingInfo={bookingInfo}
                    closestFutureCheckout={closestFutureCheckout}
                    selectedDate={selectedDate}
                    onHandleApply={onHandleApply}
                  />
                )}
              </StyledDateTimePicker>
            </DropdownContent>
          )
        );
      }}
    </Dropdown>
  );
};
